import React, { useEffect, useRef, useState, useCallback } from "react";
import "./ChattingPage.css";
import { useSearchParams } from "react-router-dom";
import VivahApi from "../../api";
import Header from "../../Components/Shared/Header/Header";
import chatWebSocket from "../../services/chatWebSocket.service";
import authService from "../../services/auth.service";
import { isPlanPurchased, isAuthenticated } from "../../utils/api-utils";

/* ── helpers ── */
function formatDateLabel(ts) {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

function getAvatarLetter(name) { return (name || "?")[0].toUpperCase(); }

function buildGroups(messages) {
  // Returns array of { date, msgs[] } — groups by calendar day
  const groups = [];
  let curDate = null, curGroup = null;
  for (const msg of messages) {
    const label = formatDateLabel(msg.timestamp || Date.now());
    if (label !== curDate) {
      curDate = label;
      curGroup = { date: label, msgs: [] };
      groups.push(curGroup);
    }
    curGroup.msgs.push(msg);
  }
  return groups;
}

function ChattingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUserId = authService.getCurrentUser()?.userId;

  const isPremiumUser = !!isPlanPurchased();

  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [wsStatus, setWsStatus] = useState("disconnected");
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [notPremiumToast, setNotPremiumToast] = useState(false);
  const [presenceMap, setPresenceMap] = useState({}); // { [partnerId]: true|false }

  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const conversationsRef = useRef([]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await VivahApi.Chat.getConversations();
      const convList = res.data?.conversations || [];
      setConversations(convList);
      conversationsRef.current = convList;
      refreshPresence(convList);
      const convParam = searchParams.get("conv");
      if (convParam) {
        const match = convList.find((c) => c.conversationId === convParam);
        if (match) {
          setSelectedConv(match);
          setMobileShowChat(true);
          try {
            const msgRes = await VivahApi.Chat.getMessages(match.conversationId);
            setMessages(msgRes.data?.messages || []);
          } catch (e) { /* ignore */ }
        }
      }
    } catch (err) {
      console.error("Failed to load conversations:", err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMessages = useCallback(async (conversationId) => {
    try {
      const res = await VivahApi.Chat.getMessages(conversationId);
      setMessages(res.data?.messages || []);
      // Backend has now marked received messages as read — update the header badge
      window.dispatchEvent(new CustomEvent("chatMessagesRead"));
    } catch (err) {
      console.error("Failed to load messages:", err);
      setMessages([]);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated()) chatWebSocket.connect();
    const unsubStatus = chatWebSocket.onStatusChange(setWsStatus);
    const unsubMsg = chatWebSocket.onMessage((data) => {
      if (data.type === "ack") {
        const msg = data.message;
        setSelectedConv((current) => {
          if (current && msg.conversationId === current.conversationId) {
            setMessages((prev) => {
              if (prev.some((m) => m.messageId === msg.messageId)) return prev;
              const idx = prev.findIndex((m) => m.pending && m.text === msg.text && m.fromUserId === msg.fromUserId);
              if (idx !== -1) { const next = [...prev]; next[idx] = msg; return next; }
              return prev;
            });
          }
          return current;
        });
      } else if (data.type === "message") {
        const msg = data.message;
        if (msg.fromUserId === currentUserId) return;
        let activeConvId = null;
        setSelectedConv((current) => {
          if (current && msg.conversationId === current.conversationId) {
            activeConvId = current.conversationId;
            setMessages((prev) => {
              if (prev.some((m) => m.messageId === msg.messageId)) return prev;
              return [...prev, msg];
            });
          } else {
            setConversations((prev) =>
              prev.map((c) =>
                c.conversationId === msg.conversationId
                  ? { ...c, lastMessage: msg.text, lastMessageAt: msg.timestamp, unreadCount: (c.unreadCount || 0) + 1 }
                  : c
              )
            );
          }
          return current;
        });
        // If the message arrived while viewing that conversation, mark it as read immediately
        if (activeConvId) loadMessages(activeConvId);
      } else if (data.type === "presence") {
        setPresenceMap((prev) => ({ ...prev, [data.userId]: data.online }));
      } else if (data.type === "error" && data.code === "NOT_PREMIUM") {
        setNotPremiumToast(true);
        setTimeout(() => setNotPremiumToast(false), 4000);
      }
    });
    loadConversations();
    return () => { unsubStatus(); unsubMsg(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadConversations]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const refreshPresence = useCallback(async (convList) => {
    const list = convList || conversationsRef.current;
    if (!list.length) return;
    const results = await Promise.allSettled(
      list.map((c) => VivahApi.Chat.getPresence(c.partnerId).then((r) => ({ id: c.partnerId, online: r.data?.online ?? false })))
    );
    const map = {};
    results.forEach((r) => { if (r.status === 'fulfilled') map[r.value.id] = r.value.online; });
    setPresenceMap((prev) => ({ ...prev, ...map }));
  }, []);

  const handleSelectConversation = (conv) => {
    setSelectedConv(conv);
    if (isPremiumUser) loadMessages(conv.conversationId);
    else setMessages([]);
    setSearchParams({ conv: conv.conversationId }, { replace: true });
    setMobileShowChat(true);
    setTimeout(() => inputRef.current?.focus(), 100);
    // Clear local unread badge immediately (backend mark-as-read happens in loadMessages)
    if (conv.unreadCount > 0) {
      setConversations((prev) =>
        prev.map((c) => c.conversationId === conv.conversationId ? { ...c, unreadCount: 0 } : c)
      );
    }
  };

  const handleBackToList = () => {
    setMobileShowChat(false);
    setSelectedConv(null);
  };

  const handleSend = () => {
    if (!msgText.trim() || !selectedConv) return;
    const tempId = `temp-${Date.now()}`;
    const trimmed = msgText.trim();
    setMessages((prev) => [...prev, {
      messageId: tempId, fromUserId: currentUserId,
      text: trimmed, timestamp: Date.now(), pending: true,
    }]);
    setMsgText("");
    const sent = chatWebSocket.sendMessage(selectedConv.partnerId, trimmed);
    if (!sent) {
      setMessages((prev) =>
        prev.map((m) => m.messageId === tempId ? { ...m, pending: false, failed: true } : m)
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const filteredConvs = conversations.filter((c) =>
    (c.partnerName || "").toLowerCase().includes(sidebarSearch.toLowerCase())
  );

  const statusLabel = wsStatus === "connected" ? "Online" : wsStatus === "connecting" ? "Connecting…" : "Offline";
  const groups = buildGroups(messages);

  return (
    <>
      <Header />
      <div className="cp">

        {/* ── Sidebar ── */}
        <aside className={`cp__sidebar${mobileShowChat ? " cp__sidebar--hidden-mobile" : ""}`}>
          <div className="cp__sidebar-header">
            <h2 className="cp__sidebar-title">Messages</h2>
            <span className={`cp__ws-badge cp__ws-badge--${wsStatus}`}>{statusLabel}</span>
          </div>
          <div className="cp__search-wrap">
            <svg className="cp__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="cp__search-input"
              placeholder="Search conversations…"
              value={sidebarSearch}
              onChange={(e) => setSidebarSearch(e.target.value)}
            />
          </div>

          {filteredConvs.length === 0 ? (
            <div className="cp__empty-list">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <p>{sidebarSearch ? "No results" : "No conversations yet"}</p>
            </div>
          ) : (
            <ul className="cp__conv-list">
              {filteredConvs.map((conv) => {
                const isActive = selectedConv?.conversationId === conv.conversationId;
                const isUnread = !isActive && conv.unreadCount > 0;
                return (
                  <li
                    key={conv.conversationId}
                    className={`cp__conv-item${isActive ? " cp__conv-item--active" : ""}${isUnread ? " cp__conv-item--unread" : ""}`}
                    onClick={() => handleSelectConversation(conv)}
                  >
                    <div className="cp__conv-avatar">
                      {getAvatarLetter(conv.partnerName)}
                      {presenceMap[conv.partnerId] !== undefined && (
                        <span className={`cp__avatar-dot${presenceMap[conv.partnerId] ? " cp__avatar-dot--online" : " cp__avatar-dot--offline"}`}></span>
                      )}
                    </div>
                    <div className="cp__conv-info">
                      <div className="cp__conv-row1">
                        <span className="cp__conv-name">{conv.partnerName || "Unknown"}</span>
                        <div className="cp__conv-row1-right">
                          {isUnread && <span className="cp__unread-badge">{conv.unreadCount}</span>}
                          {conv.lastMessageAt && (
                            <span className="cp__conv-time">
                              {new Date(conv.lastMessageAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                        </div>
                      </div>
                      {isPremiumUser ? (
                        <span className="cp__conv-last">{conv.lastMessage || "Start chatting!"}</span>
                      ) : (
                        <span className="cp__conv-last cp__conv-last--locked">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
                          Subscribe to view
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* ── Main panel ── */}
        <main className={`cp__main${mobileShowChat ? " cp__main--visible-mobile" : ""}`}>
          {selectedConv ? (
            <>
              {/* Header */}
              <div className="cp__chat-header">
                <button className="cp__back-btn" onClick={handleBackToList} aria-label="Back">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="20" height="20">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <div className="cp__chat-avatar">{getAvatarLetter(selectedConv.partnerName)}</div>
                <div className="cp__chat-header-info">
                  <span className="cp__chat-name">{selectedConv.partnerName}</span>
                  {presenceMap[selectedConv.partnerId] !== undefined && (
                    <span className={`cp__presence${presenceMap[selectedConv.partnerId] ? " cp__presence--online" : " cp__presence--offline"}`}>
                      <span className="cp__presence-dot"></span>
                      {presenceMap[selectedConv.partnerId] ? "Online" : "Offline"}
                    </span>
                  )}
                </div>
              </div>

              {/* Messages */}
              {!isPremiumUser ? (
                <div className="cp__messages cp__messages--locked">
                  <div className="cp__locked-wall">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="44" height="44" className="cp__locked-icon">
                      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                    </svg>
                    <h3 className="cp__locked-title">{selectedConv.partnerName} sent you a message</h3>
                    <p className="cp__locked-sub">Subscribe to a plan to read and reply to messages.</p>
                    <a href="/update" className="cp__locked-btn">Upgrade Now →</a>
                  </div>
                </div>
              ) : (
                <div className="cp__messages" ref={messagesContainerRef}>
                  {messages.length === 0 && (
                    <div className="cp__empty-chat">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" width="56" height="56">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                      <p>No messages yet.<br/>Say hello to {selectedConv.partnerName}!</p>
                    </div>
                  )}

                  {groups.map((group) => (
                    <React.Fragment key={group.date}>
                      <div className="cp__date-sep"><span>{group.date}</span></div>
                      {group.msgs.map((msg, i) => {
                        const isSent = msg.fromUserId === currentUserId;
                        const prev = group.msgs[i - 1];
                        const isFirstInRun = !prev || prev.fromUserId !== msg.fromUserId;
                        return (
                          <div
                            key={msg.messageId || i}
                            className={`cp__msg-row cp__msg-row--${isSent ? "sent" : "received"}${isFirstInRun ? " cp__msg-row--first" : ""}`}
                          >
                            {!isSent && (
                              <div className="cp__msg-avatar" style={{ visibility: isFirstInRun ? "visible" : "hidden" }}>
                                {getAvatarLetter(selectedConv.partnerName)}
                              </div>
                            )}
                            <div className={`cp__bubble cp__bubble--${isSent ? "sent" : "received"}`}>
                              <span className="cp__bubble-text">{msg.text}</span>
                              <span className="cp__bubble-meta">
                                {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                                {isSent && (
                                  <span className="cp__tick">
                                    {msg.pending ? " ○" : msg.failed ? " ✕" : " ✓"}
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Input / Upgrade CTA */}
              {notPremiumToast && (
                <div className="cp__not-premium-toast">Upgrade your plan to send messages.</div>
              )}
              {isPremiumUser ? (
                <footer className="cp__footer">
                  <input
                    ref={inputRef}
                    className="cp__input"
                    value={msgText}
                    type="text"
                    placeholder={`Message ${selectedConv.partnerName}…`}
                    onChange={(e) => setMsgText(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                  <button
                    className="cp__send-btn"
                    onClick={handleSend}
                    disabled={!msgText.trim()}
                    aria-label="Send"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                    </svg>
                  </button>
                </footer>
              ) : (
                <footer className="cp__upgrade-footer">
                  <div className="cp__upgrade-cta">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" className="cp__upgrade-icon">
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                    </svg>
                    <span>You have messages waiting.&nbsp;</span>
                    <a href="/update" className="cp__upgrade-link">Upgrade your plan to reply →</a>
                  </div>
                </footer>
              )}
            </>
          ) : (
            <div className="cp__no-selection">
              <div className="cp__no-selection-art">
                <svg viewBox="0 0 120 120" fill="none" width="120" height="120">
                  <circle cx="60" cy="60" r="58" fill="#fce4ec" />
                  <path d="M30 45 Q30 30 45 30 H75 Q90 30 90 45 V65 Q90 80 75 80 H65 L55 92 L55 80 H45 Q30 80 30 65 Z" fill="#e91e63" opacity="0.15" stroke="#e91e63" strokeWidth="2"/>
                  <circle cx="47" cy="55" r="4" fill="#e91e63"/>
                  <circle cx="60" cy="55" r="4" fill="#e91e63"/>
                  <circle cx="73" cy="55" r="4" fill="#e91e63"/>
                </svg>
              </div>
              <h3 className="cp__no-selection-title">Your Messages</h3>
              <p className="cp__no-selection-sub">Pick a conversation from the left to start chatting</p>
            </div>
          )}
        </main>
      </div>
    </>
  );
}

export default ChattingPage;
